import RootAdmin from "../../../components/layouts/RootAdmin";
import { Button, Card, CardBody, CardHeader, Chip, Divider, Input, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader } from "@nextui-org/react";
import { EyeIcon, PlusIcon } from "@heroicons/react/24/outline";
import { getFetcher, getSelectCountryFetcher, getSelectFetcher, getSelectFetcherJenisB3 } from "../../../services/api";
import useSWR from "swr";
import useCustomNavigate from "../../../hooks/useCustomNavigate";
import { useMemo, useState } from "react";
import useNotification from "../../../hooks/notification/useNotification";
import { Controller } from "react-hook-form";
import ReactSelect from "../../../components/elements/ReactSelect";
import { calculateRegistrasiRekomendasiProcessingDaysWithTanggalPengajuan, hasValidRole } from "../../../services/helpers";
import { getCountryName } from "../../../services/helpers";
import useAuth from "../../../hooks/useAuth";
import RolesAccess from "../../../enums/roles";
import ClientDataGrid from "../../../components/elements/ClientDataGrid";
import ControlledInput from "../../../components/elements/ControlledInput";
import ControlledReactSelect from "../../../components/elements/ControlledReactSelect";

export default function IndexPage() {
  const { user, roles } = useAuth();

  const { getNotificationDetailPath } = useCustomNavigate();
  const { data, isLoading, mutate } = useSWR(
    hasValidRole(roles, [RolesAccess.DIREKTUR, RolesAccess.SUPER_ADMIN, RolesAccess.PIC_NOTIFIKASI])
      ? `/api/notifikasi?returnAll=true`
      : `/api/notifikasi?companyIds=${user.companies?.map((company) => company.companyId).join(",")}&returnAll=true`,
    getFetcher
  );
  const { data: dataCompany, isLoading: isLoadingCompany } = useSWR(`/api/company/search-company?returnAll=true`, getFetcher);
  const { data: dataBahan, isLoading: isLoadingBahan } = useSWR(`/api/data-master/bahan-b3?returnAll=true`, getFetcher);
  const { data: dataCountry, isLoading: isLoadingCountry } = useSWR(`/api/countries`, getFetcher);

  const {
    onCloseForm,
    onSubmitForm,
    modalForm: { onOpenModalForm, isOpenModalForm, onOpenChangeModalForm },
    hookForm: {
      register,
      control,
      handleSubmit,
      formState: { errors, isSubmitting },
    },
  } = useNotification({ mutate });

  const columns = useMemo(
    () => [
      {
        field: "referenceNumber",
        headerName: "Nomor Referensi",
      },
      {
        field: "dataBahanB3NamaBahan",
        headerName: "Nama Bahan Kimia",
        valueGetter: (value, row) => row?.dataBahanB3?.namaBahanKimia,
      },
      {
        field: "dataBahanB3NamaDagang",
        headerName: "Nama Dagang",
        valueGetter: (value, row) => row?.dataBahanB3?.namaDagang,
      },
      {
        field: "negaraAsal",
        headerName: "Eksportir/Negara Asal",
        valueGetter: (value, row) => getCountryName(row?.negaraAsal, "en", { select: "official" }), // console.log(row.negaraAsal),
      },
      {
        field: "companyName",
        headerName: "Importir",
        valueGetter: (value, row) => row?.company?.name,
      },
      {
        field: "statusHistoryNotes",
        headerName: "Riwayat Surat",
        valueGetter: (value, row) => row?.draftSuratNotifikasiId?.map((item) => item?.nomorSuratFormatted).join(", "),
      },

      {
        field: "lamaProses",
        headerName: "Lama Proses",
        renderCell: (params) => {
          const lamaProses = calculateRegistrasiRekomendasiProcessingDaysWithTanggalPengajuan(params.row.tanggal_pengajuan, params.row.tanggal_selesai);

          // Gunakan if-else untuk perbandingan logika
          if (lamaProses < 45) {
            return (
              <Chip color="success" variant="flat" size="sm">
                {lamaProses} Hari
              </Chip>
            );
          } else {
            return (
              <Chip color="danger" variant="flat" size="sm">
                {lamaProses} Hari
              </Chip>
            );
          }
        },
      },

      {
        field: "status",
        headerName: "Status",
        renderCell: (params) => {
          switch (params?.row?.status) {
            case "Selesai":
              return (
                <Chip color="success" variant="flat" size="sm">
                  Selesai
                </Chip>
              ); // Render a specific component or value for 'value1'
            case "Dibatalkan":
              return (
                <Chip color="danger" variant="flat" size="sm">
                  Dibatalkan
                </Chip>
              ); // Render a specific component or value for 'value2' // Render a
            default:
              return (
                <Chip color="secondary" variant="flat" size="sm">
                  {params.row.status}
                </Chip>
              ); // Default rendering for any other values
          }
        },
      },
      {
        field: "action",
        headerName: "Aksi",
        renderCell: (params) => {
          if (hasValidRole(roles, [RolesAccess.SUPER_ADMIN, RolesAccess.PIC_NOTIFIKASI])) {
            return (
              <div className="flex items-center gap-1">
                <Button size="sm" color="primary" isIconOnly onClick={() => getNotificationDetailPath(params?.row?.id)}>
                  <EyeIcon className="size-4" />
                </Button>
              </div>
            );
          } else {
            return "-";
          }
        },
        sortable: false,
        filterable: false,
      },
    ],
    [getNotificationDetailPath]
  );

  return (
    <RootAdmin>
      <Card className="w-full mt-3" radius="sm">
        <CardHeader>Daftar Notifikasi</CardHeader>
        <Divider />
        <CardBody className="w-full h-[530px] p-5">
          {}
          <div className="mb-5">
            {hasValidRole(roles, [RolesAccess.SUPER_ADMIN, RolesAccess.PIC_NOTIFIKASI]) && (
              <Button onPress={onOpenModalForm} size="sm" color="primary" startContent={<PlusIcon className="size-4 stroke-2" />}>
                Tambah
              </Button>
            )}
          </div>
          <ClientDataGrid
            columns={columns}
            rows={data?.data}
            isLoading={isLoading}
            initialState={{
              columns: {
                columnVisibilityModel: {
                  dataBahanB3NamaBahan: false,
                  dataBahanB3NamaDagang: false,
                  statusHistoryNotes: false,
                },
              },
            }}
          />
        </CardBody>
      </Card>

      <Modal
        scrollBehavior="inside"
        isOpen={isOpenModalForm}
        onOpenChange={onOpenChangeModalForm}
        onClose={onCloseForm}
        isDismissable={false}
        isKeyboardDismissDisabled={false}
        style={{
          overflow: "visible", // Pastikan modal tidak membatasi dropdown
        }}
      >
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader>Tambah Notifikasi</ModalHeader>
              <ModalBody>
                <form onSubmit={handleSubmit(onSubmitForm)}>
                  <div className="flex flex-col gap-2 mb-6">
                    <ControlledInput control={control} label="Nomor Referensi" name={"referenceNumber"} placeholder="..." isRequired />
                    <ControlledInput control={control} label="Tanggal Email Masuk" name={"tanggalDiterima"} placeholder="..." isRequired type="date" />
                    <ControlledReactSelect
                      control={control}
                      isLoading={isLoadingBahan}
                      label="Jenis B3"
                      name="databahanb3Id"
                      menuPortalTarget={document.body}
                      options={dataBahan?.data?.map((item) => ({ label: `${item.casNumber} - ${item.namaBahanKimia}`, value: item.id }))}
                    />
                    <ControlledReactSelect
                      control={control}
                      isLoading={isLoadingCompany}
                      label="Perusahaan"
                      name="companyId"
                      menuPortalTarget={document.body}
                      options={dataCompany?.data?.map((item) => ({ label: item.name, value: item.id }))}
                    />
                    <ControlledReactSelect
                      control={control}
                      isLoading={isLoadingCountry}
                      label="Negara Asal"
                      name="negaraAsal"
                      menuPortalTarget={document.body}
                      options={dataCountry?.map((item) => ({ label: item.name, value: item.code2 }))}
                    />
                  </div>
                  <div className="flex items-center gap-1">
                    <Button isLoading={isSubmitting} isDisabled={isSubmitting} type="submit" color="primary">
                      Tambah
                    </Button>
                    <Button isDisabled={isSubmitting} color="danger" variant="faded" onPress={onClose}>
                      Batal
                    </Button>
                  </div>
                </form>
              </ModalBody>
              <ModalFooter></ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </RootAdmin>
  );
}
